<template>
  <div class="i-bundle-plan-options">
    <div class="heading">Choose your <strong>Success Path</strong></div>
    <div class="subheading">
      Both plans include your free 4th month. Split payments with Klarna, no credit check required.
    </div>

    <div class="plans">
      <div
        v-for="(plan, idx) in bundlePlans"
        :key="idx"
        class="plan-wrapper"
      >
        <div
          v-if="plan.theme === 'accelerated'"
          class="most-popular"
        >
          Most Popular
        </div>
        <div
          class="plan"
          :class="plan.theme"
        >
          <div class="plan-header">
            <img
              class="badge"
              aria-hidden="true"
              :src="plan.badgeIcon"
              alt="Badge"
            />
            <div class="titles">
              <div class="title">{{ plan.header }}</div>
              <div class="sessions">{{ plan.sessionsLabel }}</div>
            </div>
          </div>

          <div class="price-block">
            <div class="price">
              ${{ plan.price.toLocaleString() }}
              <span
                v-if="plan.oldPrice"
                class="old-price"
              >
                ${{ plan.oldPrice.toLocaleString() }}
              </span>
            </div>
            <div class="second-line">
              <span class="duration">for 4 months •</span>
              <span class="save">Save ${{ plan.saveAmount.toLocaleString() }}</span>
            </div>
            <div class="effective">{{ plan.effectiveRate }}</div>
          </div>

          <div
            class="klarna-box"
            role="group"
            aria-label="Klarna payment schedule"
          >
            <div class="klarna-head">
              <span>Pay with Klarna</span>
              <span class="pill">4 payments</span>
            </div>
            <ul class="klarna-list">
              <li
                v-for="row in plan.klarna"
                :key="row.label"
              >
                <span class="label">• {{ row.label }}</span>
                <span class="amount">${{ row.amount }}</span>
              </li>
            </ul>
            <div class="klarna-footer">
              <span class="zero">0% Interest Rate</span>
              <span class="total">Total: ${{ plan.price.toFixed(2) }}</span>
            </div>
          </div>

          <ul class="perks">
            <li
              v-for="perk in plan.perks"
              :key="perk"
              :class="{ muted: plan.excludedPerks && plan.excludedPerks.includes(perk) }"
            >
              <img
                v-if="!plan.excludedPerks || !plan.excludedPerks.includes(perk)"
                alt="icon"
                :src="IconCheckGreen"
              />
              <img
                v-if="plan.excludedPerks && plan.excludedPerks.includes(perk)"
                alt="icon"
                :src="IconXBlack"
              />
              <span>{{ perk }}</span>
            </li>
          </ul>

          <iBaseButton
            class="cta"
            :text="plan.actionText"
            :disabled="processing"
            width="100%"
            text-color="white"
            border-radius="16px"
            @clicked="() => onPlanClicked(idx, plan)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue"
import { useRoute } from "vue-router"

import api from "@/services/apiClient"
import StripePrice from "@/shared/enums/StripePrice"
import GtmService, { UserFlow } from "@/services/gtmService"
import { UserFlow as UserFlowEnum } from "@/services/gtmService"

import getPaymentAction from "@/composables/getPaymentAction"

// TYPES
import { CheckoutSessionItem } from "@/shared/types/CheckoutSession"
import StripePaymentMethod from "@/shared/enums/StripePaymentMethod"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import IconCrownGradient from "@/assets/icons/icon-crown-gradient.svg"
import IconLigthningBlue from "@/assets/icons/icon-lightning-blue.svg"
import IconCheckGreen from "@/assets/icons/icon-check-green.svg"
import IconXBlack from "@/assets/icons/icon-x-black.svg"

// ROUTES
const route = useRoute()

// REFS
// Internal processing state to avoid multiple concurrent clicks
const processing = ref<boolean>(false)

interface KlarnaRow {
  label: string
  amount: string
}
interface BundlePlan {
  header: string
  sessionsLabel: string
  price: number
  oldPrice: number
  saveAmount: number
  effectiveRate: string
  klarna: KlarnaRow[]
  perks: string[]
  excludedPerks?: string[]
  actionText: string
  theme: "core" | "accelerated"
  badgeIcon: string
  action: () => Promise<void> | void
}
// Discount identifier from route or session (if present)
const discountIdentifier: string | undefined =
  (route.query["promo_code"] as string | undefined) || sessionStorage.getItem("promo_code") || undefined

// overwrite payment method: used for QA if klarna doesn't work in your region
const getPaymentMethod = <StripePaymentMethod>new URLSearchParams(window.location.search).get("paymentMethod")
const paymentMethods = getPaymentMethod ? [getPaymentMethod] : [StripePaymentMethod.KLARNA]
const firePaymentAction = (price: StripePrice, userFlow: UserFlow) => {
  const action = getPaymentAction([price], userFlow, () => {}, discountIdentifier, paymentMethods)
  return action()
}

const bundlePlans: BundlePlan[] = [
  {
    header: "Core",
    sessionsLabel: "2 sessions/month",
    price: 540,
    oldPrice: 720,
    saveAmount: 180,
    effectiveRate: "Effective rate: $135.00/month",
    klarna: [
      { label: "Due today", amount: "135.00" },
      { label: "In 2 weeks", amount: "135.00" },
      { label: "In 4 weeks", amount: "135.00" },
      { label: "In 6 weeks", amount: "135.00" }
    ],
    perks: [
      "Up to 1 hour of 1:1 coaching each month",
      "Custom-built action plan",
      "Mock interviews with your coach",
      "Resume and Linkedin guidance",
      "Weekly live group coaching",
      "Premium Career report with personalized insights",
      "Twice the coaching for faster results"
    ],
    excludedPerks: ["Twice the coaching for faster results"],
    actionText: "Start Core Plan",
    theme: "core",
    badgeIcon: IconLigthningBlue,
    action: () => firePaymentAction(StripePrice.COACHING_CORE_BUNDLE, UserFlowEnum.COACHING)
  },
  {
    header: "Accelerated",
    sessionsLabel: "4 sessions/month",
    price: 1050,
    oldPrice: 1400,
    saveAmount: 350,
    effectiveRate: "Effective rate: $262.50/month",
    klarna: [
      { label: "Due today", amount: "262.50" },
      { label: "In 2 weeks", amount: "262.50" },
      { label: "In 4 weeks", amount: "262.50" },
      { label: "In 6 weeks", amount: "262.50" }
    ],
    perks: [
      "Up to 2 hours of 1:1 coaching each month",
      "Custom-built action plan",
      "Mock interviews with your coach",
      "Resume and LinkedIn guidance",
      "Weekly live group coaching",
      "Premium Career report with personalized insights",
      "Twice the coaching for faster results"
    ],
    actionText: "Start Accelerated Plan",
    theme: "accelerated",
    badgeIcon: IconCrownGradient,
    action: () => firePaymentAction(StripePrice.COACHING_ACCELERATED_BUNDLE, UserFlowEnum.COACHING)
  }
]

async function onPlanClicked(idx: number, plan: BundlePlan) {
  if (processing.value) return
  try {
    processing.value = true
    await plan.action()
  } catch (error) {
    console.error("Failed to start checkout session", error)
  } finally {
    processing.value = false
  }
}

/**
 * Send a GTM event when the user views the coaching plans - in this case on page load
 */
const sendGTMEvent = async () => {
  try {
    const response = await api.stripe.getPrices([
      StripePrice.COACHING_CORE_BUNDLE,
      StripePrice.COACHING_ACCELERATED_BUNDLE
    ])
    const pricesArr = response.prices
    const itemsShownToClient: CheckoutSessionItem[] = []

    for (const price of pricesArr) {
      itemsShownToClient.push({
        id: price.productId,
        price: price.price / 100,
        quantity: 1,
        name: price.name
      })
    }

    if (itemsShownToClient.length > 0) {
      await GtmService.viewItemListEvent(UserFlow.COACHING, itemsShownToClient)
    }
  } catch (error) {
    console.error("Failed to load prices", error)
  }
}

onMounted(async () => await sendGTMEvent())
</script>

<style scoped lang="scss">
.i-bundle-plan-options {
  @include flex(column, flex-start, stretch);

  gap: 16px;
  margin: 44px auto;

  @media (min-width: $breakpoint-lg) {
    margin: 64px auto;
  }

  .heading {
    @include display-lg;

    font-weight: 400;
    color: $comet950;
    text-align: center;
  }

  .subheading {
    @include text-lg;

    color: $comet950;
    text-align: center;
    margin-top: -6px;
    margin-bottom: 56px;
  }

  .plans {
    display: grid;
    gap: 28px;
    grid-template-columns: 1fr;
    max-width: 1024px;
    margin: 0 auto;

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: 1fr 1fr;
      align-items: stretch;
    }
  }

  .most-popular {
    @include text-xs;

    background: $blue-purple-gradient;
    box-shadow: 0 0 7.383px 1.477px rgb(74 157 255 / 47%);
    position: absolute;
    width: 124px;
    text-align: center;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    padding: 4px 8px;
    border-radius: 999px;
    font-weight: 600;
  }

  .plan-wrapper {
    position: relative;
    padding: 0;
    margin: 0;
  }

  .plan {
    background: $white;
    border: 1px solid $black100;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 10px 20px rgb(0 0 0 / 6%);
    display: flex;
    flex-direction: column;
    gap: 24px;

    .plan-header {
      @include flex(row, flex-start, center);

      gap: 12px;

      .badge {
        width: 48px;
        height: 48px;
        border-radius: 16px;
        background: #4a9dff33;
        padding: 12px;
      }

      .titles {
        .title {
          @include text-xxl;

          font-weight: 700;
          color: $black900;
        }

        .sessions {
          @include text-sm;

          color: $black400;
        }
      }
    }

    &.accelerated {
      border-color: #ff6b6b33;
      box-shadow:
        0 20px 40px -15px rgb(237 69 125 / 25%),
        0 8px 16px -8px rgb(15 19 26 / 15%);

      .plan-header {
        .badge {
          background: $blue-purple-gradient-light-faded;
        }
      }
    }

    .price-block {
      .price {
        @include display-lg;

        font-weight: 700;
        color: $black900;
      }

      .old-price {
        @include display-xs;

        color: $black400;
        font-weight: 400;
        text-decoration: line-through;
      }

      .second-line {
        @include flex(row, flex-start, center);

        gap: 8px;

        .duration {
          @include text-sm;

          color: $black400;
        }

        .save {
          @include text-sm;

          color: #2abb67;
          font-weight: 600;
        }
      }

      .effective {
        @include text-xs;

        color: $black400;
        font-weight: 400;
      }
    }

    .klarna-box {
      background: rgb(243 244 246 / 50%);
      border: 1px solid #ece7ff;
      border-radius: 16px;
      padding: 17px;

      .klarna-head {
        @include flex(row, space-between, center);
        @include text-sm;

        color: $black700;

        .pill {
          @include text-xs;

          background: $blue-purple-gradient;
          color: white;
          padding: 4px 8px;
          border-radius: 999px;
          font-weight: 600;
        }
      }

      .klarna-list {
        margin: 10px 0;
        padding: 8px 10px;

        li {
          @include flex(row, space-between, center);
          @include text-sm;

          color: $black800;
          padding: 6px 0;
          margin-left: -6px;

          .label {
            color: $black700;
          }

          .amount {
            font-weight: 600;
          }
        }
      }

      .klarna-footer {
        @include flex(row, space-between, center);

        border-top: 1px solid #e2e4e9;
        padding-top: 10px;

        .total {
          @include text-sm;

          font-weight: 700;
        }

        .zero {
          color: #2abb67;

          @include text-xs;

          font-weight: 600;
        }
      }
    }

    .perks {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 12px;

      li {
        @include text-sm;

        color: $black700;
        display: flex;
        gap: 8px;
        align-items: center;
      }
    }

    .perks li.muted {
      color: $comet400;
    }

    .cta {
      margin-top: 4px;
      background: $blue-purple-gradient;
    }
  }
}
</style>
